import { useEffect, useId, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { Position } from "../../api/types";
import { uuidV4 } from "../../lib/uuid";

// RespondModal sends a single free-text message to the position's hiring
// contact. Unlike the CRUD forms it mints a FRESH idempotency key per submit
// attempt: the server persists failure responses too, so reusing a key would
// replay a transient email failure forever. Success is an inline "Message
// sent" state (the app has no toast system), then the modal closes.
export function RespondModal({ position, onClose }: { position: Position; onClose: () => void }) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const messageId = useId();
  const overlayDismiss = useOverlayDismiss(onClose, submitting);

  useEffect(() => {
    if (!sent) {
      return;
    }
    const timer = window.setTimeout(onClose, 1600);
    return () => window.clearTimeout(timer);
  }, [sent, onClose]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = message.trim();
    if (trimmed === "" || submitting || sent) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.respondToPosition(position.id, trimmed, uuidV4());
      setSent(true);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" role="presentation" {...overlayDismiss}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="modal__header">
          <h2 id={titleId}>Respond to {position.title}</h2>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose} disabled={submitting}>
            ×
          </button>
        </header>
        <div className="modal__body">
          {sent ? (
            <p className="respond-modal__sent" role="status">
              Message sent ✓
            </p>
          ) : (
            <form onSubmit={handleSubmit}>
              <p>
                Refer someone, share info, or express interest — your message goes straight to the
                hiring contact.
              </p>
              <div className="form-row">
                <label htmlFor={messageId}>Message</label>
                <textarea
                  id={messageId}
                  rows={5}
                  maxLength={4000}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="I know someone great for this…"
                  required
                  autoFocus
                />
              </div>
              {error !== null && <p className="form-row__error">{error}</p>}
              <div className="modal__footer">
                <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={message.trim() === "" || submitting}
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === "email_unavailable") {
      return "Email delivery isn't configured in this environment.";
    }
    if (caught.code === "rate_limited") {
      return "You've reached the response limit for this position — try again tomorrow.";
    }
    if (caught.code === "position_closed") {
      return "This position has been closed.";
    }
    if (caught.code === "not_found") {
      return "This position no longer exists.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Couldn't send your response.";
}
